import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';

const SLOT_TO_AREA = {
  slot0: 'row1col0',
  slot1: 'row1col1',
  slot2: 'row1col2',
  slot3: 'row2col0',
  slot4: 'row2col1',
  slot5: 'row2col2',
  slot6: 'fullwidth',
};

const LABEL_POS = {
  row1col0:  { bottom: 16, left: 0, right: 0, textAlign: 'center' },
  row1col1:  { bottom: 16, left: 0, right: 0, textAlign: 'center' },
  row1col2:  { bottom: 16, left: 0, right: 0, textAlign: 'center' },
  row2col0:  { bottom: 16, left: 0, right: 0, textAlign: 'center' },
  row2col1:  { bottom: 16, left: 0, right: 0, textAlign: 'center' },
  row2col2:  { bottom: 16, left: 0, right: 0, textAlign: 'center' },
  fullwidth: { bottom: 18, left: 0, right: 0, textAlign: 'center' },
};

const GRADIENT       = 'linear-gradient(to top, rgba(0,0,0,0.55) 0%, transparent 50%)';
const GRADIENT_WIDE  = 'linear-gradient(to top, rgba(0,0,0,0.40) 0%, transparent 55%)';

const Cell = ({ item, slotId, style = {} }) => {
  const area = SLOT_TO_AREA[slotId];
  const lp   = LABEL_POS[area] || { bottom: 16, left: 14 };
  const gr   = slotId === 'slot6' ? GRADIENT_WIDE : GRADIENT;

  const base = {
    position: 'relative',
    overflow: 'hidden',
    display: 'block',
    background: '#e5e7eb',
    flexShrink: 0,
    ...style,
  };

  if (!item) return <div style={base} />;

  return (
    <Link
      to={item.link}
      data-testid={`bento-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
      style={base}
      className="group"
    >
      <img
        src={item.image}
        alt={item.label}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'top center',
          transition: 'transform 0.7s ease',
        }}
        className="group-hover:scale-105"
      />
      <div style={{ position: 'absolute', inset: 0, background: gr }} />
      <div style={{ position: 'absolute', ...lp }}>
        <span style={{
          color: '#fff',
          fontSize: 'clamp(0.65rem, 1.5vw, 0.85rem)',
          letterSpacing: '0.22em',
          fontWeight: 300,
          fontFamily: 'inherit',
          textShadow: '0 1px 8px rgba(0,0,0,0.85)',
          whiteSpace: 'nowrap',
        }}>
          {item.label}
        </span>
      </div>
    </Link>
  );
};

const BentoCategoryGrid = () => {
  const [bySlot, setBySlot] = useState({});

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    try {
      const r = await api.get('/bento-categories');
      const map = {};
      r.data.forEach(item => { map[item.grid_position] = item; });
      setBySlot(map);
    } catch (e) { console.error(e); }
  };

  if (Object.keys(bySlot).length === 0) return null;

  const GAP = 6;

  return (
    <section className="py-10 md:py-16 lg:py-24 bg-white" data-testid="bento-category-section">
      <div className="container mx-auto px-3 md:px-6 lg:px-8">

        <h2 className="font-heading text-lg md:text-xl lg:text-2xl font-bold text-[#2A0000] text-center mb-6 md:mb-10 tracking-[0.25em] md:tracking-[0.3em] uppercase">
          SHOP BY CATEGORIES
        </h2>

        {/*
          ── MOBILE (< 640px): single column stack, all cells equal height ──
          ── TABLET (640–1023px): 2-column grid ──
          ── DESKTOP (≥ 1024px): original 3-col layout ──
        */}

        {/* MOBILE — single column, each cell 260px tall */}
        <div className="flex flex-col sm:hidden" style={{ gap: GAP }}>
          {['slot0','slot1','slot2','slot3','slot4','slot5'].map(sid => (
            <Cell key={sid} item={bySlot[sid]} slotId={sid}
              style={{ width: '100%', height: 260 }} />
          ))}
          {/* slot6 wide landscape */}
          <Cell item={bySlot['slot6']} slotId="slot6"
            style={{ width: '100%', height: 200 }} />
        </div>

        {/* TABLET — 2 columns */}
        <div className="hidden sm:grid lg:hidden"
          style={{ gridTemplateColumns: '1fr 1fr', gap: GAP }}>
          {['slot0','slot1','slot2','slot3','slot4','slot5'].map(sid => (
            <Cell key={sid} item={bySlot[sid]} slotId={sid}
              style={{ height: 300 }} />
          ))}
          {/* slot6 spans both columns */}
          <div style={{ gridColumn: '1 / -1' }}>
            <Cell item={bySlot['slot6']} slotId="slot6"
              style={{ width: '100%', height: 220 }} />
          </div>
        </div>

        {/* DESKTOP — original 3-col layout with full heights */}
        <div className="hidden lg:block">
          {/* Row 1 */}
          <div style={{ display: 'flex', gap: GAP, height: 420, marginBottom: GAP }}>
            {['slot0','slot1','slot2'].map(sid => (
              <Cell key={sid} item={bySlot[sid]} slotId={sid}
                style={{ flex: 1, height: 420 }} />
            ))}
          </div>
          {/* Row 2 */}
          <div style={{ display: 'flex', gap: GAP, height: 420, marginBottom: GAP }}>
            {['slot3','slot4','slot5'].map(sid => (
              <Cell key={sid} item={bySlot[sid]} slotId={sid}
                style={{ flex: 1, height: 420 }} />
            ))}
          </div>
          {/* Row 3 — full width */}
          <Cell item={bySlot['slot6']} slotId="slot6"
            style={{ width: '100%', height: 500 }} />
        </div>

      </div>
    </section>
  );
};

export default BentoCategoryGrid;